/**
 * Плашка ошибки конвейера в полосе «Что дальше» окна макета.
 *
 * Вместо строки кодов — сгруппированный список «площадка: причина — N товаров
 * (категории)» и подсказка, что делать. Исходный текст остаётся под
 * «Техническим текстом» с копированием: по имени кода ошибку ищут в логе.
 */

import React, { useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, Undo2 } from 'lucide-react'
import { CopyButton } from '../../../../components/ui/CopyButton'
import { cn } from '../../../../lib/cn'
import {
  parsePipelineError,
  productsCountLabel,
  type PipelineErrorGroup,
} from './pipelineError'

export interface PipelineNoticeValue {
  kind: 'error' | 'warning' | 'returned'
  text: string
}

interface PipelineErrorPlateProps {
  notice: PipelineNoticeValue
  /** Сырой pipeline_error: из него строится разбор по площадкам. */
  rawError?: string | null
  /** Категория (префикс артикула), чтобы писать «PL1, PT2», а не десять артикулов. */
  prefixOf?: (article: string) => string | null
  onOpenProduct?: (article: string) => void
  className?: string
}

const MAX_ARTICLES_INLINE = 5

const GroupLine: React.FC<{
  group: PipelineErrorGroup
  prefixOf?: (article: string) => string | null
  onOpenProduct?: (article: string) => void
}> = ({ group, prefixOf, onOpenProduct }) => {
  const [open, setOpen] = useState(false)
  const prefixes = prefixOf
    ? [...new Set(group.articles.map((article) => prefixOf(article)).filter((value): value is string => Boolean(value)))]
    : []
  const scope = prefixes.length > 0 ? prefixes : group.articles
  const shown = scope.slice(0, MAX_ARTICLES_INLINE).join(', ')
  const rest = scope.length - MAX_ARTICLES_INLINE
  return (
    <li className="space-y-0.5">
      <div>
        <span className="font-medium">
          {group.marketplaceLabel ? `${group.marketplaceLabel}: ` : ''}
          {group.short}
        </span>
        {' — '}
        <span className="tabular-nums">{productsCountLabel(group.articles.length)}</span>
        <span className="font-mono text-xs"> ({shown}{rest > 0 ? ` и ещё ${rest}` : ''})</span>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="ml-2 text-xs underline decoration-dotted underline-offset-2 hover:no-underline"
        >
          {open ? 'скрыть артикулы' : 'показать артикулы'}
        </button>
      </div>
      {open && (
        <div className="flex flex-wrap gap-x-2 gap-y-0.5 pl-1">
          {group.articles.map((article) =>
            onOpenProduct ? (
              <button
                key={article}
                type="button"
                title="Открыть товар"
                onClick={() => onOpenProduct(article)}
                className="font-mono text-xs underline underline-offset-2 hover:no-underline"
              >
                {article}
              </button>
            ) : (
              <span key={article} className="font-mono text-xs">
                {article}
              </span>
            ),
          )}
        </div>
      )}
      {group.action && <div className="text-xs opacity-90">Что делать: {group.action}</div>}
    </li>
  )
}

/**
 * Разобранная ошибка конвейера: заголовок, группы «площадка: причина — N
 * товаров», подсказка и свёрнутый «Технический текст» с копированием.
 * Одна на плашку окна и на ленту «История».
 */
export const PipelineErrorDetails: React.FC<{
  raw: string
  prefixOf?: (article: string) => string | null
  onOpenProduct?: (article: string) => void
  /** Заголовок над списком; в ленте истории он избыточен. */
  showHeadline?: boolean
}> = ({ raw, prefixOf, onOpenProduct, showHeadline = true }) => {
  const [techOpen, setTechOpen] = useState(false)
  const view = parsePipelineError(raw)
  if (!view) return null
  const technical = view.technical !== (view.plain ?? '') ? view.technical : null

  return (
    <div className="space-y-1.5">
      {showHeadline && <b className="block font-semibold">{view.headline ?? 'Что случилось'}</b>}
      {view.groups.length > 0 ? (
        <ul className="m-0 list-none space-y-1.5 p-0">
          {view.groups.map((group) => (
            <GroupLine key={group.key} group={group} prefixOf={prefixOf} onOpenProduct={onOpenProduct} />
          ))}
        </ul>
      ) : (
        <div>
          {view.plain}
          {view.plainAction && <div className="mt-0.5 text-xs opacity-90">Что делать: {view.plainAction}</div>}
        </div>
      )}

      {technical && (
        <div>
          <button
            type="button"
            aria-expanded={techOpen}
            onClick={() => setTechOpen((value) => !value)}
            className="inline-flex items-center gap-1 text-xs underline decoration-dotted underline-offset-2 hover:no-underline"
          >
            {techOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Технический текст
          </button>
          {techOpen && (
            <div className="mt-1 flex items-start gap-1 rounded border border-gray-200 bg-white/60 p-2 text-gray-700 dark:border-zinc-700 dark:bg-zinc-900/60 dark:text-zinc-300">
              <pre className="m-0 min-w-0 flex-1 whitespace-pre-wrap break-words font-mono text-[11px]">
                {technical}
              </pre>
              <CopyButton value={technical} label="технический текст ошибки" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const PipelineErrorPlate: React.FC<PipelineErrorPlateProps> = ({
  notice,
  rawError,
  prefixOf,
  onOpenProduct,
  className,
}) => {
  const isError = notice.kind === 'error'

  return (
    <div
      role={isError ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-md px-3 py-2 text-[0.8125rem]',
        isError
          ? 'bg-red-100/70 text-red-800 dark:bg-red-950/40 dark:text-red-300'
          : 'bg-amber-100/70 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
        className,
      )}
    >
      {notice.kind === 'returned' ? (
        <Undo2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        {isError ? (
          <PipelineErrorDetails
            raw={rawError ?? notice.text}
            prefixOf={prefixOf}
            onOpenProduct={onOpenProduct}
          />
        ) : (
          <div>
            <b className="font-semibold">
              {notice.kind === 'warning' ? 'Часть карточек не создана: ' : 'Комментарий продакта: '}
            </b>
            {notice.text}
          </div>
        )}
      </div>
    </div>
  )
}

export default PipelineErrorPlate
